/**
 * Minijuego "El reto de las 100 dominadas" — mecánica de ritmo.
 *
 * CONTRATO (común a los 3 minijuegos de la beta):
 *  - init(data?: { volverA?: string }): si `volverA` viene, al salir se hace
 *    `this.scene.start(volverA)`; si NO viene, fue lanzado desde
 *    MissionScene (pausada escuchando el evento global) y al salir
 *    SOLO se emite 'minijuego-terminado' y se hace `this.scene.stop()`.
 *  - HUD visible: `ensureRunning(this, SCENE_KEYS.HUD)` + `bringToTop`.
 *  - Al terminar: panel de resultado, se aplican recompensas vía GameState
 *    y se llama `GameState.registrarPartida('mg-dominadas')`. Botón
 *    SEGUIR → emite y sale según `volverA`.
 *
 * RECOMPENSAS (proporcionales al esfuerzo):
 *  - dinero   = floor(N / 5)
 *  - respeto  = floor(N / 10); bonus +10 respeto si N >= 100
 *  - exito    = (N >= 20)
 *
 * Sin assets binarios: Graphics + texto/emoji. La barra espaciadora
 * equivale al tap (jugable también en escritorio).
 */

import Phaser from 'phaser';
import { GameState } from '@/engine/GameState';
import { ensureRunning, SCENE_KEYS } from '@/engine/nav';
import { getMinigame } from '@/data/minijuegos';

// ─────────────────────────── Paleta y layout ────────────────────────────

const COLOR_FONDO = 0x14141c;
const COLOR_PANEL = 0x22222e;
const COLOR_BOTON = 0x2a2a38;
const COLOR_BOTON_HOVER = 0x38384a;
const COLOR_BORDE_PANEL = 0x38384a;
const COLOR_TEXTO = '#ffffff';
const COLOR_TEXTO_DIM = '#8a8a95';
const COLOR_ZONA_VERDE = 0x27ae60;
const COLOR_ZONA_GRIS = 0x38384a;
const COLOR_CURSOR = 0xf1c40f;
const COLOR_POSTE = 0x6a6a78;
const COLOR_BARRA = 0xb8b8c5;
const COLOR_MUNECO = 0xf5d76e;
const COLOR_MUNECO_CUERPO = 0xe8c64f;
const COLOR_DIM_OVERLAY = 0x000000;

const HUD_ALTO = 100;
const MARGEN = 24;

const OBJETIVO = 100;          // dominadas → victoria inmediata
const FALLOS_MAX = 3;          // al tercer fallo se termina la tanda
const EXITO_MIN = 20;          // >= 20 dominadas = exito
const BONUS_RESPETO_100 = 10;  // bonus si se llega a 100

// ───────── Curva de dificultad en función de las dominadas ────────────

interface Dificultad {
  /** Velocidad del cursor en pixeles/segundo sobre el barra de ritmo. */
  velocidad: number;
  /** Ancho en pixeles de la zona buena (verde) en el barra de ritmo. */
  anchoZonaVerde: number;
}

/** 320 → 800 px/s (+40 cada 10) y 200 → 80 px de zona (-20 cada 20). Mínimos
 *  respetables para que llegar a 100 sea difícil pero posible. */
function dificultadPara(dominadas: number): Dificultad {
  const velocidad = Math.min(800, 320 + Math.floor(dominadas / 10) * 40);
  const anchoZonaVerde = Math.max(80, 200 - Math.floor(dominadas / 20) * 20);
  return { velocidad, anchoZonaVerde };
}

// ─────────────────────── Datos de entrada del escena ───────────────────

interface DominadasData {
  volverA?: string;
}

// ──────────────────────────── La escena ──────────────────────────────

export class MinigameDominadasScene extends Phaser.Scene {
  private volverA: string | undefined;
  private terminada = false;

  // Estado de partida.
  private dominadas = 0;
  private fallos = 0;
  private exito = false;

  // Cursor: posición normalizada (-1 izq, +1 der) y dirección.
  private cursorFrac = -1;
  private cursorDir = 1;
  private dificultad: Dificultad = dificultadPara(0);

  // Geometría del barra de ritmo (calculada en create()).
  private ritmoLeftX = 0;
  private ritmoRightX = 0;
  private ritmoY = 0;

  // Geometría del pull-up rig (poste + barra), centro del muñeco.
  private barraIzqX = 0;
  private barraDerX = 0;
  private barraY = 0;

  // UI reutilizable.
  private pullUpGfx!: Phaser.GameObjects.Graphics;   // postes + barra horizontal
  private munecoGfx!: Phaser.GameObjects.Graphics;   // muñeco (cabeza+tronco+brazos+piernas)
  private barraRitmoGfx!: Phaser.GameObjects.Graphics;
  private cursorGfx!: Phaser.GameObjects.Graphics;
  private contadorTxt!: Phaser.GameObjects.Text;
  private fallosTxt!: Phaser.GameObjects.Text;

  // Animación del muñeco: 0 = colgado (bajo), 1 = pulled-up (arriba).
  // Cada frame `munecoFrac` se acerca a `munecoObjetivoFrac`.
  private munecoObjetivoFrac = 0.5;
  private munecoFrac = 0.5;
  private munecoAnimId = 0; // bumper para invalidar retornos encolados

  constructor() {
    super('mg-dominadas');
  }

  // ──────────────── Lifecycle ────────────────

  init(data?: DominadasData): void {
    this.volverA = data?.volverA;
    this.terminada = false;
    this.dominadas = 0;
    this.fallos = 0;
    this.exito = false;
    this.cursorFrac = -1;
    this.cursorDir = 1;
    this.dificultad = dificultadPara(0);
    this.munecoObjetivoFrac = 0.5;
    this.munecoFrac = 0.5;
    this.munecoAnimId = 0;
  }

  create(): void {
    const { width, height } = this.scale;
    this.cameras.main.setBackgroundColor(COLOR_FONDO);

    // Chequeo de racha al entrar al reto: si el último día completado no es
    // ni hoy ni ayer, la racha se rompe (el jugador se saltó más de un día).
    // No es un side effect invasivo: solo limpia el contador en memoria y
    // dispara el autosave vía GameState, así el HUD y el save quedan alineados.
    this.comprobarRachaRota();

    ensureRunning(this, SCENE_KEYS.HUD);
    this.scene.bringToTop(SCENE_KEYS.HUD);

    // Limpieza defensiva de listeners de teclado cuando la escena se apaga.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.input.keyboard?.off('keydown-SPACE');
    });

    // Título.
    this.add
      .text(width / 2, HUD_ALTO + 38, 'EL RETO DE LAS 100 DOMINADAS', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5);

    // Zona del pull-up (decorativa + feedback por tap).
    this.construirPullUp(width);

    // Cabecera: contador grande + fallos.
    this.construirCabecera(width);

    // Barra de ritmo y cursor (centrado en mitad inferior).
    this.construirRitmo(width, height);

    // Hint inferior.
    this.add
      .text(width / 2, height - 60, 'TOCA sobre la zona verde · [ESPACIO] también vale', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: COLOR_TEXTO_DIM,
      })
      .setOrigin(0.5);

    this.configurarInput();
  }

  update(_t: number, delta: number): void {
    if (this.terminada) return;
    this.moverCursor(delta);
    this.interpolarMuneco();
    this.dibujarCursor();
    this.dibujarMuneco();
  }

  // ──────────────── Pull-up rig (poste + barra + muñeco) ────────────────

  private construirPullUp(width: number): void {
    const cx = width / 2;
    this.barraIzqX = cx - 110;
    this.barraDerX = cx + 110;
    this.barraY = HUD_ALTO + 110; // 210, justo bajo el título

    this.pullUpGfx = this.add.graphics();
    // 2 postes verticales que sostienen la barra.
    this.pullUpGfx.fillStyle(COLOR_POSTE, 1);
    this.pullUpGfx.fillRoundedRect(this.barraIzqX - 5, this.barraY - 4, 10, 140, 3);
    this.pullUpGfx.fillRoundedRect(this.barraDerX - 5, this.barraY - 4, 10, 140, 3);
    // Barra horizontal (lo que se agarra) — ligeramente más larga que los postes.
    this.pullUpGfx.fillStyle(COLOR_BARRA, 1);
    this.pullUpGfx.fillRoundedRect(
      this.barraIzqX - 18,
      this.barraY - 8,
      this.barraDerX - this.barraIzqX + 36,
      16,
      6,
    );

    this.munecoGfx = this.add.graphics();
  }

  /** Y de la cabeza del muñeco: cuando munecoFrac=0 cuelga bajo (y grande),
   *  cuando munecoFrac=1 ha subido (dominada, y pequeño). Interpolable. */
  private munecoCabezaY(): number {
    return this.barraY + 30 - this.munecoFrac * 60;
  }

  private dibujarMuneco(): void {
    const cx = (this.barraIzqX + this.barraDerX) / 2;
    const barraY = this.barraY;
    const headY = this.munecoCabezaY();
    const radioCabeza = 14;
    const cuerpoTop = headY + radioCabeza;
    const cuerpoBottom = cuerpoTop + 32;

    this.munecoGfx.clear();

    // Brazos agarrando la barra (líneas diagonales desde el hombro a la barra).
    this.munecoGfx.lineStyle(4, COLOR_MUNECO, 1);
    this.munecoGfx.beginPath();
    this.munecoGfx.moveTo(cx - 10, cuerpoTop + 4);
    this.munecoGfx.lineTo(cx - 16, barraY);
    this.munecoGfx.strokePath();
    this.munecoGfx.beginPath();
    this.munecoGfx.moveTo(cx + 10, cuerpoTop + 4);
    this.munecoGfx.lineTo(cx + 16, barraY);
    this.munecoGfx.strokePath();

    // Cabeza (círculo).
    this.munecoGfx.fillStyle(COLOR_MUNECO, 1);
    this.munecoGfx.fillCircle(cx, headY, radioCabeza);

    // Cuerpo (línea vertical).
    this.munecoGfx.lineStyle(5, COLOR_MUNECO_CUERPO, 1);
    this.munecoGfx.beginPath();
    this.munecoGfx.moveTo(cx, cuerpoTop);
    this.munecoGfx.lineTo(cx, cuerpoBottom);
    this.munecoGfx.strokePath();

    // Piernas (V corta).
    this.munecoGfx.lineStyle(3, COLOR_MUNECO_CUERPO, 1);
    this.munecoGfx.beginPath();
    this.munecoGfx.moveTo(cx - 6, cuerpoBottom);
    this.munecoGfx.lineTo(cx, cuerpoBottom + 10);
    this.munecoGfx.lineTo(cx + 6, cuerpoBottom);
    this.munecoGfx.strokePath();
  }

  private interpolarMuneco(): void {
    // Suavizado: el muñeco persigue su objetivo cada frame (sin tween
    // explícito → no hay que limpiar tweens al cambiar de objetivo).
    this.munecoFrac = Phaser.Math.Linear(this.munecoFrac, this.munecoObjetivoFrac, 0.18);
  }

  /** Empuja la posición objetivo del muñeco durante `mantenerMs`, y al
   *  cabo de ese tiempo vuelve al colgado neutro (0.5). Si entra un tap
   *  más reciente, el retorno se invalida por id. */
  private animarMuneco(subirFrac: number, mantenerMs: number): void {
    this.munecoObjetivoFrac = subirFrac;
    this.munecoAnimId += 1;
    const id = this.munecoAnimId;
    this.time.delayedCall(mantenerMs, () => {
      if (this.terminada) return;
      if (id !== this.munecoAnimId) return; // un tap más reciente ya reescribió
      this.munecoObjetivoFrac = 0.5;
    });
  }

  // ──────────────── Cabecera: contador grande + corazones ────────────────

  private construirCabecera(width: number): void {
    this.contadorTxt = this.add
      .text(width / 2, HUD_ALTO + 360, `${this.dominadas} / ${OBJETIVO}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '48px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, HUD_ALTO + 408, 'DOMINADAS', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: COLOR_TEXTO_DIM,
      })
      .setOrigin(0.5);

    // Los corazones se posicionan después, al construir el barra de ritmo
    // (necesitamos ritmoLeftX). Aquí solo creamos el objeto con defaults.
    this.fallosTxt = this.add
      .text(0, 0, this.fallosComoCorazones(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '24px',
      })
      .setOrigin(0.5, 0.5);
  }

  private fallosComoCorazones(): string {
    const perdidos = this.fallos;
    const quedan = FALLOS_MAX - perdidos;
    return '❤'.repeat(quedan) + '🖤'.repeat(perdidos);
  }

  private refrescarFallos(): void {
    this.fallosTxt.setText(this.fallosComoCorazones());
  }

  // ──────────────── Barra de ritmo + cursor ────────────────

  private construirRitmo(width: number, height: number): void {
    this.ritmoLeftX = 50;
    this.ritmoRightX = width - 50;
    this.ritmoY = Math.round(height * 0.59); // ~755 en 1280

    this.barraRitmoGfx = this.add.graphics();
    this.dibujarBarraRitmo();

    this.cursorGfx = this.add.graphics();
    this.dibujarCursor();

    // Colocamos los corazones a la izquierda del barra de ritmo.
    this.fallosTxt.setPosition(this.ritmoLeftX - 32, this.ritmoY);

    // Etiqueta "ZONA VERDE" sobre la barra.
    const cx = (this.ritmoLeftX + this.ritmoRightX) / 2;
    this.add
      .text(cx, this.ritmoY - 36, 'ZONA VERDE', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#9be7b6',
      })
      .setOrigin(0.5);
  }

  private dibujarBarraRitmo(): void {
    const cx = (this.ritmoLeftX + this.ritmoRightX) / 2;
    this.barraRitmoGfx.clear();
    // Pista gris.
    this.barraRitmoGfx.fillStyle(COLOR_ZONA_GRIS, 1);
    this.barraRitmoGfx.fillRoundedRect(
      this.ritmoLeftX,
      this.ritmoY - 18,
      this.ritmoRightX - this.ritmoLeftX,
      36,
      8,
    );
    // Zona buena verde.
    this.barraRitmoGfx.fillStyle(COLOR_ZONA_VERDE, 1);
    this.barraRitmoGfx.fillRoundedRect(
      cx - this.dificultad.anchoZonaVerde / 2,
      this.ritmoY - 18,
      this.dificultad.anchoZonaVerde,
      36,
      8,
    );
  }

  private moverCursor(delta: number): void {
    const dt = delta / 1000;
    const anchoTotal = this.ritmoRightX - this.ritmoLeftX;
    const fraccionRecorridoPorSegundo = this.dificultad.velocidad / anchoTotal;
    this.cursorFrac += this.cursorDir * fraccionRecorridoPorSegundo * dt;
    if (this.cursorFrac >= 1) {
      this.cursorFrac = 1;
      this.cursorDir = -1;
    } else if (this.cursorFrac <= -1) {
      this.cursorFrac = -1;
      this.cursorDir = 1;
    }
  }

  private dibujarCursor(): void {
    const x = Phaser.Math.Linear(
      this.ritmoLeftX,
      this.ritmoRightX,
      (this.cursorFrac + 1) / 2,
    );
    this.cursorGfx.clear();
    this.cursorGfx.fillStyle(COLOR_CURSOR, 1);
    // Barra vertical centrada en el barra de ritmo.
    this.cursorGfx.fillRoundedRect(x - 4, this.ritmoY - 26, 8, 52, 3);
  }

  // ──────────────── Input ────────────────

  private configurarInput(): void {
    const { width, height } = this.scale;
    // Zona invisible a pantalla completa: cualquier tap es un intento.
    const zona = this.add
      .zone(width / 2, height / 2, width, height)
      .setInteractive({ useHandCursor: true });
    zona.on('pointerdown', () => this.intentarTap());

    this.input.keyboard?.on('keydown-SPACE', () => this.intentarTap());
  }

  /** Evalúa si el cursor está dentro de la zona buena y aplica el resultado. */
  private intentarTap(): void {
    if (this.terminada) return;
    const cx = (this.ritmoLeftX + this.ritmoRightX) / 2;
    const cursorX = Phaser.Math.Linear(
      this.ritmoLeftX,
      this.ritmoRightX,
      (this.cursorFrac + 1) / 2,
    );
    const dentro = Math.abs(cursorX - cx) <= this.dificultad.anchoZonaVerde / 2;
    if (dentro) this.aplicarAcierto();
    else this.aplicarFallo();
  }

  private aplicarAcierto(): void {
    this.dominadas += 1;
    this.contadorTxt.setText(`${this.dominadas} / ${OBJETIVO}`);
    // El muñeco sube brevemente y vuelve a colgar.
    this.animarMuneco(1, 170);

    if (this.dominadas >= OBJETIVO) {
      this.victoria();
      return;
    }

    // Recalcular dificultad (velocidad cada 10, zona cada 20).
    const nueva = dificultadPara(this.dominadas);
    let redraw = false;
    if (nueva.anchoZonaVerde !== this.dificultad.anchoZonaVerde) {
      this.dificultad.anchoZonaVerde = nueva.anchoZonaVerde;
      redraw = true;
    }
    this.dificultad.velocidad = nueva.velocidad;
    if (redraw) this.dibujarBarraRitmo();
  }

  private aplicarFallo(): void {
    this.fallos += 1;
    this.refrescarFallos();
    // El muñeco baja (se aleja de la barra) y vuelve.
    this.animarMuneco(0, 220);
    if (this.fallos >= FALLOS_MAX) {
      this.terminarTanda();
    }
  }

  private victoria(): void {
    this.exito = true;
    // Celebración simple: pulso del contador.
    this.tweens.add({
      targets: this.contadorTxt,
      scale: 1.35,
      duration: 220,
      yoyo: true,
      ease: 'Quad.easeInOut',
    });
    this.time.delayedCall(300, () => this.terminarTanda());
  }

  // ──────────────── Racha ────────────────

  /**
   * Devuelve la fecha ISO (YYYY-MM-DD) de `d - 1 día` en hora local. Lo
   * necesitamos para tolerar la "ventana de gracia" del reto diario: si el
   * jugador entrenó ayer, la racha sigue viva hoy aunque la última fecha
   * registrada sea de ayer (no se rompe hasta que se salta más de un día).
   */
  private ayerIso(): string {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  /**
   * Si la racha está activa pero la última fecha completada NO es ni hoy ni
   * ayer, la resetea. Se llama al entrar a la escena; no rompe la partida
   * porque solo toca el contador, pero deja el estado coherente con la
   * realidad del jugador.
   */
  private comprobarRachaRota(): void {
    const racha = GameState.racha;
    if (racha.diasConsecutivos <= 0) return;
    const hoyIso = new Date().toISOString().slice(0, 10);
    if (racha.ultimaFechaCompletada === hoyIso) return;
    if (racha.ultimaFechaCompletada === this.ayerIso()) return;
    GameState.reiniciarRacha();
  }

  // ──────────────── Fin de tanda + recompensa ────────────────

  private terminarTanda(): void {
    if (this.terminada) return;
    this.terminada = true;
    this.exito = this.dominadas >= EXITO_MIN;

    const respeto = Math.floor(this.dominadas / 10);
    const dinero = Math.floor(this.dominadas / 5);
    const bonus = this.dominadas >= OBJETIVO ? BONUS_RESPETO_100 : 0;
    const respetoTotal = respeto + bonus;

    GameState.addRespeto(respetoTotal);
    GameState.addDinero(dinero);
    GameState.registrarPartida('mg-dominadas');

    // Reto diario: completar las 100 dominadas cuenta el día en la racha.
    // El guard de mismo día ya vive dentro de GameState.registrarDiaCompletado.
    let retoCompletado = false;
    if (this.dominadas >= OBJETIVO) {
      const hoyIso = new Date().toISOString().slice(0, 10);
      retoCompletado = GameState.racha.ultimaFechaCompletada !== hoyIso;
      GameState.registrarDiaCompletado(hoyIso);
    }

    this.renderPanelResultado(respetoTotal, dinero, retoCompletado);
  }

  private renderPanelResultado(respeto: number, dinero: number, retoCompletado: boolean): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const cy = height / 2;

    // Tinte de fondo para tapar la escena sin destruirla.
    this.add
      .rectangle(width / 2, height / 2, width, height, COLOR_DIM_OVERLAY, 0.55)
      .setOrigin(0.5)
      .setDepth(100);

    // Tarjeta.
    const panelAncho = width - MARGEN * 2;
    // Si sumamos el reto diario al panel necesitamos un pelín más de alto.
    const panelAlto = retoCompletado ? 440 : 380;
    const tarjeta = this.add.graphics();
    tarjeta.fillStyle(COLOR_PANEL, 1);
    tarjeta.fillRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    tarjeta.lineStyle(2, COLOR_BORDE_PANEL, 1);
    tarjeta.strokeRoundedRect(-panelAncho / 2, -panelAlto / 2, panelAncho, panelAlto, 20);
    tarjeta.setPosition(cx, cy);
    tarjeta.setDepth(101);

    // Título y nombre del minijuego (este último desde el catálogo).
    const mg = getMinigame('mg-dominadas');
    const titulo =
      this.dominadas >= OBJETIVO
        ? '¡VICTORIA!'
        : this.exito
          ? '¡BUENA TANDA!'
          : 'SIGUE ENTRENANDO';

    this.add
      .text(cx, cy - 130, titulo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '30px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5)
      .setDepth(110);
    this.add
      .text(cx, cy - 90, mg.nombre, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '17px',
        color: COLOR_TEXTO_DIM,
      })
      .setOrigin(0.5)
      .setDepth(110);

    this.add
      .text(cx, cy - 40, `${this.dominadas} dominadas`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '30px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5)
      .setDepth(110);

    // Recompensas formateadas igual que MissionScene.
    const partes: string[] = [];
    if (respeto > 0) partes.push(`✊ +${respeto}`);
    if (dinero > 0) partes.push(`💶 +${dinero}`);
    const resumen = partes.length > 0 ? partes.join('  ·  ') : 'Sin recompensas';
    this.add
      .text(cx, cy + 30, resumen, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '32px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5)
      .setDepth(110);

    // Línea extra del reto diario: solo si esta tanda ha sido la que ha
    // sumado el día a la racha (o si ya veníamos de una racha > 0). Usamos el
    // valor vivo de GameState porque el registro ya se hizo en terminarTanda.
    if (retoCompletado || GameState.racha.diasConsecutivos > 0) {
      this.add
        .text(
          cx,
          cy + 70,
          `🔥 Reto diario completado · racha: ${GameState.racha.diasConsecutivos} días`,
          {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '18px',
            color: '#ffb454',
          },
        )
        .setOrigin(0.5)
        .setDepth(110);
    }

    // Botón SEGUIR (estilo coherente con los botones de MissionScene).
    const botonAncho = 260;
    const botonAlto = 72;
    // Si hemos sumado la línea del reto diario, empujamos el botón un poco
    // hacia abajo para que no se solape con ella.
    const botonY = retoCompletado ? cy + 160 : cy + 130;
    const boton = this.add.graphics();
    const dibujar = (color: number) => {
      boton.clear();
      boton.fillStyle(color, 1);
      boton.fillRoundedRect(-botonAncho / 2, -botonAlto / 2, botonAncho, botonAlto, 18);
    };
    dibujar(COLOR_BOTON);
    boton.setPosition(cx, botonY);
    boton.setDepth(110);

    this.add
      .text(cx, botonY, 'SEGUIR', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        fontStyle: 'bold',
        color: COLOR_TEXTO,
      })
      .setOrigin(0.5)
      .setDepth(111);

    const zona = this.add
      .zone(cx, botonY, botonAncho, botonAlto)
      .setInteractive({ useHandCursor: true });
    zona.on('pointerover', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerout', () => dibujar(COLOR_BOTON));
    zona.on('pointerdown', () => dibujar(COLOR_BOTON_HOVER));
    zona.on('pointerup', () => this.salir());
    zona.setDepth(112);
  }

  /** Emite el evento global de fin y sale según `volverA`. */
  private salir(): void {
    if (this.volverA) {
      this.scene.start(this.volverA);
    } else {
      this.game.events.emit('minijuego-terminado', { exito: this.exito });
      this.scene.stop();
    }
  }
}
